import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import { searchCommunities } from '../../actions/communitiesActions'
import { getAllOrganizations } from '../../actions/organizationsActions'
import { isValidEmail } from '../../utility/helperFunctions'
import Select from '../common/form/Select'
import OrganizationsSelect from '../common/form/OrganizationsSelect'
import Input from '../common/form/Input'
import Modal from '../common/Modal'
import CancelButton from '../common/buttons/CancelButton'
import SubmitButton from '../common/buttons/SubmitButton'

import classes from './InviteCoachModal.module.scss'

const InviteCoachModal = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const organizations = useSelector(state => state.organizations.organizations)

  const customErrorMessages = {
    firstName: t('coaches.invite.errors.firstName'),
    lastName: t('coaches.invite.errors.lastName'),
    email: t('coaches.invite.errors.email'),
    communityId: t('coaches.invite.errors.communityOrOrganization'),
  }

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [selectedCommunity, setSelectedCommunity] = useState(null)
  const [organizationIds, setOrganizationIds] = useState([])

  const [communityOptions, setCommunityOptions] = useState([])
  const [communitySearchTerm, setCommunitySearchTerm] = useState('')
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const communityId = selectedCommunity?.value || ''
  const communityRequired = organizationIds.length === 0

  useEffect(() => {
    dispatch(getAllOrganizations())
  }, [dispatch])

  const formValid = () => {
    const errors = {}
    const fieldValues = { firstName, lastName, email };
    ['firstName', 'lastName', 'email'].forEach((field) => {
      if (String(fieldValues[field] || '').trim() === '') { errors[field] = customErrorMessages[field] }
    })
    if (email && !isValidEmail(email)) { errors.email = customErrorMessages.email }
    if (communityRequired && !communityId) { errors.communityId = customErrorMessages.communityId }
    setErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formValid()) { return }

    const inviteData = {
      first_name: firstName,
      last_name: lastName,
      email,
      community_id: communityId || null,
      organization_ids: organizationIds,
    }

    setIsSubmitting(true)
    onSubmit(inviteData).finally(() => setIsSubmitting(false))
  }

  const handleChange = (field, value) => {
    const setters = { firstName: setFirstName, lastName: setLastName, email: setEmail }
    setters[field](value)
    setErrors(prev => ({ ...prev, [field]: null }))
  }

  const communityOrganizationNames = (selectedCommunity?.organizations || []).map(o => o.name).join(', ')

  // organizations added because of the chosen community follow that community when it changes
  const handleSelectChange = (selectedOption) => {
    const previousCommunityOrganizationIds = (selectedCommunity?.organizations || []).map(o => o.id)
    const nextCommunityOrganizationIds = (selectedOption?.organizations || []).map(o => o.id)

    setSelectedCommunity(selectedOption || null)
    setOrganizationIds(prev => [...new Set([...prev.filter(id => !previousCommunityOrganizationIds.includes(id)), ...nextCommunityOrganizationIds])])
    setErrors(prev => ({ ...prev, communityId: null }))
  }

  const handleOrganizationsChange = (ids) => {
    setOrganizationIds(ids)
    setErrors(prev => ({ ...prev, communityId: null }))
  }

  const handleCommunitySearch = (inputValue, { action }) => {
    if (action !== 'input-change') { return }

    setCommunitySearchTerm(inputValue)
    if (inputValue && inputValue.length >= 3) {
      setIsLoadingCommunities(true)
      dispatch(searchCommunities(inputValue))
        .then(response => setCommunityOptions(
          response.data.communities.map(c => ({ value: c.id, label: c.name, organizations: c.organizations || [] }))
        ))
        .finally(() => setIsLoadingCommunities(false))
    } else {
      setCommunityOptions([])
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={classes.inviteCoachModalContent}
    >
      <div className={classes.inviteCoachModal}>
        <div className='d-flex flex-column mb-5'>
          <h2 className={classes.modalTitle}>{t('coaches.invite.title')}</h2>
          <p className={classes.modalSubtitle}>
            {t('coaches.invite.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className={classes.modalForm}>
          <div className='row pb-4'>
            <div className='col-12'>
              <div className='row'>
                <div className='col-12 col-md-6 mb-3'>
                  <label htmlFor='firstName'>
                    {t('common.field.firstName')} <span className='required-text'>*</span>
                  </label>
                  <Input
                    id='firstName'
                    type='text'
                    value={firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    error={errors.firstName}
                  />
                </div>
                <div className='col-12 col-md-6 mb-3'>
                  <label htmlFor='lastName'>
                    {t('common.field.lastName')} <span className='required-text'>*</span>
                  </label>
                  <Input
                    id='lastName'
                    type='text'
                    value={lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    error={errors.lastName}
                  />
                </div>
              </div>

            </div>

            <div className='col-12 mb-3'>
              <label htmlFor='email'>
                {t('common.field.email')} <span className='required-text'>*</span>
              </label>
              <Input
                id='email'
                type='text'
                value={email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={errors.email}
              />
            </div>

            <div className='col-12 mb-3'>
              <label htmlFor='organizations'>{t('coaches.invite.organizations')}</label>
              <OrganizationsSelect
                inputId='organizations'
                organizations={organizations}
                selectedIds={organizationIds}
                onChange={handleOrganizationsChange}
                placeholder={t('coaches.invite.organizationsPlaceholder')}
                noOptionsMessage={t('coaches.invite.noOrganizationsFound')}
              />
              <small className={classes.fieldHint}>{t('coaches.invite.organizationsHint')}</small>
            </div>

            <div className='col-12 mb-3'>
              <label htmlFor='community'>
                {t('coaches.common.community')} {communityRequired && <span className='required-text'>*</span>}
              </label>
              <Select
                inputId='community'
                isClearable
                value={selectedCommunity || null}
                onChange={(selectedOption) => handleSelectChange(selectedOption)}
                onInputChange={handleCommunitySearch}
                options={communityOptions}
                placeholder={t('coaches.invite.communityPlaceholder')}
                isLoading={isLoadingCommunities}
                filterOption={null}
                noOptionsMessage={() => communitySearchTerm.length < 3 ? t('coaches.invite.typeToSearch') : t('coaches.invite.noCommunitiesFound')}
                error={errors.communityId}
              />
              {communityOrganizationNames && (
                <small className={classes.fieldHint}>{t('coaches.invite.communityOrganizationsNote', { names: communityOrganizationNames })}</small>
              )}
              {!communityId && <small className={classes.fieldHint}>{t('coaches.invite.freeCoachHelp')}</small>}
            </div>
          </div>

          <div className='row pt-4'>
            <div className='col-6'>
              <CancelButton onClick={onClose} buttonMessage={t('common.cancel')} />
            </div>
            <div className='col-6'>
              <SubmitButton type='submit' buttonMessage={t('coaches.invite.sendInvite')} loading={isSubmitting} noTopMargin />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}

InviteCoachModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default InviteCoachModal
